import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import {
    BaseOrder,
    BundleCandidate,
    DocuSignForm,
    ErrorWrapper,
    OrderSigningMethod
} from '@trade-platform/ui-shared';
import { DetailViewState } from '../../overview';
import { fileType, orderStatus } from '@trade-platform/lib-enums';
import { AixOrderStepComponent } from '../step/order-step';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixExpansionPanelComponent,
    AixHeaderSectionComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { RemoteData } from 'ngx-remotedata';
import { ActivatedRoute } from '@angular/router';
import { AixTemplatesComponent } from '@advisor-ui/app-components';

import { AixPdfGenerationComponent } from '../../pdf-generation/pdf-generation';
import { AixFormUploadComponent } from '../../../../form-upload/form-upload';
import { AixOrderProcessDeliveryComponent } from '../../delivery/delivery';
import { AixMapSignaturesComponent } from '../../docusign/map-signatures/map-signatures';
import { AixOrderProcessDocuSignRecipientsComponent } from '../../docusign/recipients/recipients';
import { AixOrderProcessDocuSignSignaturesComponent } from '../../docusign/signatures/signatures';
import { AixOrderProcessESignComponent } from '../../e-sign/e-sign';
import { AixOrderProcessWetSignComponent } from '../../wet-sign/wet-sign';
import { AixFileUploadComponent } from '../../../../file-upload/file-upload';
import { areFormUploadsCompletedInCurrentStep } from '../../utils/order-utils';
import { AixOrdersDetailUploadDocumentsComponent } from '../../../../detail/upload-documents/upload-documents';

@Component({
    selector: 'aix-signatures-step',
    templateUrl: './signatures-step.html',
    styleUrls: ['./signatures-step.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixTemplatesComponent,
        AixPdfGenerationComponent,
        AixHeaderSectionComponent,
        AixDataTestingDirective,
        AixButtonComponent,
        AixFormUploadComponent,
        AixOrderProcessDeliveryComponent,
        AixMapSignaturesComponent,
        AixOrderProcessDocuSignRecipientsComponent,
        AixOrderProcessDocuSignSignaturesComponent,
        AixOrderProcessESignComponent,
        AixOrderProcessWetSignComponent,
        AixExpansionPanelComponent,
        AixOrdersDetailUploadDocumentsComponent
    ]
})
export class AixSignaturesStepComponent extends AixOrderStepComponent implements OnInit, OnDestroy {
    readonly reducerSuffix = this.storeFacade.type;

    orderFormUpload = viewChild<AixFileUploadComponent>(AixFileUploadComponent);
    orderFormUploadPrincipal = viewChild<AixFileUploadComponent>(AixFileUploadComponent);
    orderFileUploadRef = viewChild<AixFileUploadComponent>(AixFileUploadComponent);

    uploadButtonType = BUTTON_TYPE.link;

    viewState: DetailViewState = 'preview';

    filesGenerated = true;
    orderStatus = orderStatus;
    signaturesCompleted = false;
    eSignWithWetSignEnabled = false;
    eSignWithWetSignCompleted = false;
    wetSignOnly = false;
    subscriptions: Subscription[] = [];
    linkButtonType = BUTTON_TYPE.link;

    recipientError: string;

    // Bundled signing (aix-ui#5491). Candidates are the other eligible orders; the advisor ticks a
    // subset and sends them as one DocuSign package. bundleError holds the fail-closed rejection text.
    bundleCandidates: BundleCandidate[] = [];
    selectedCandidateIds = new Set<string>();
    multiProduct = false;
    bundleError: string;
    // An ambiguous send outcome (timeout / network / server error): the send may have been accepted, so
    // the UI must not assert "nothing sent" or auto-resend — it prompts the advisor to check status.
    bundleOutcomeUnknown = false;
    // A partial result: some members did not send. Surfaced rather than hidden behind the anchor refresh.
    bundlePartialWarning = '';
    // In-flight guard: set true synchronously before dispatching a bundle send and reset on
    // success/failure, so a rapid double-confirm cannot fire a second concurrent send-bundle POST
    // (the effect uses switchMap, which would otherwise cancel/race an in-flight request). (aix-ui#5491)
    bundleSendInProgress = false;

    states: Observable<RemoteData<any, string | ErrorWrapper>>[] = [
        this.storeFacade.orderDocusignSendRemoteData$,
        this.appStore.select(state => state.customTemplates),
        this.appStore.select(state => state.customTemplate)
    ];

    ngOnInit() {
        this.subscriptions.push(
            (this.route.parent as ActivatedRoute).queryParams.subscribe(params => {
                if (params.mappingCompleted === 'true') {
                    this.storeFacade.actions.finishMapSignatures.dispatch({
                        orderId: this.orderId
                    });
                    this.router.navigate(
                        this.storeFacade.routes.process.collectSignatures(this.orderId)
                    );
                }
            }),
            this.storeFacade.actions.sendDocusign.success$.subscribe(action => {
                this.loadOrder();
            }),
            this.storeFacade.actions.sendDocusign.failure$.subscribe(action => {
                if (action.payload.error.options.status === 422) {
                    this.recipientError = action.payload.error.options.errorText;
                }
            }),
            this.storeFacade.actions.mapSignatures.success$.subscribe(action => {
                // Redirect user to DocuSign map signatures url
                window.location.href = action.payload;
            }),
            this.storeFacade.actions.finishMapSignatures.success$.subscribe(action => {
                this.loadOrder();
            }),
            this.storeFacade.actions.signingMethod.success$.subscribe(action => {
                this.loadOrder();
            }),
            this.storeFacade.actions.takeOrderOffline.success$.subscribe(action => {
                this.downloadTradePackage();
                this.loadOrder();
            }),
            this.storeFacade.actions.removeDocument.success$.subscribe(action => {
                this.loadOrder();
            }),
            this.storeFacade.actions.getOrder.success$.subscribe(action => {
                this.onOrderUpdated(action.payload.order);
            })
        );

        // Bundled signing (aix-ui#5491) — only in the buy flow, where the facade exposes these actions.
        if (this.storeFacade.actions.loadBundleCandidates && this.storeFacade.actions.sendBundle) {
            this.subscriptions.push(
                this.storeFacade.actions.loadBundleCandidates.success$.subscribe(action => {
                    this.bundleCandidates = action.payload.candidates ?? [];
                    this.multiProduct = !!action.payload.multiProduct;
                    this.ref.detectChanges();
                }),
                this.storeFacade.actions.sendBundle.success$.subscribe(action => {
                    this.bundleSendInProgress = false;
                    // Reconcile exactly the returned members: surface any that did NOT send rather than
                    // hiding a partial result behind the anchor refresh.
                    const failed = action.payload?.failed ?? [];
                    // A `failed` member is NOT necessarily safe to resend: its envelope may be in an
                    // ambiguous/possibly-live state (a send or void whose result was unknown), kept
                    // claimed by the backend to prevent a duplicate. So surface it for attention but do
                    // NOT direct a resend — tell the advisor to check each order's status first.
                    this.bundlePartialWarning = failed.length
                        ? `${failed.length} selected order(s) were not sent with the bundle. Check each order's status before taking further action — do not resend until it shows it is ready.`
                        : '';
                    this.loadOrder();
                }),
                this.storeFacade.actions.sendBundle.failure$.subscribe(action => {
                    this.bundleSendInProgress = false;
                    if (action.payload.error?.options?.status === 422) {
                        // Definitive fail-closed rejection BEFORE delivery: nothing was sent. Show it,
                        // drop the selection, and re-fetch candidates so the advisor re-checks the state.
                        this.bundleError =
                            action.payload.error?.options?.errorText ??
                            'Nothing was sent — re-check the selection and try again.';
                        this.selectedCandidateIds.clear();
                        this.storeFacade.actions.loadBundleCandidates?.dispatch({
                            orderId: this.orderId
                        });
                    } else {
                        // Ambiguous outcome (timeout / network / server error): the send may already have
                        // been accepted by DocuSign. Do NOT assert "nothing was sent" or auto-resend —
                        // prompt the advisor to check the orders' status, and refresh to reconcile.
                        this.bundleOutcomeUnknown = true;
                        this.loadOrder();
                    }
                    this.ref.detectChanges();
                })
            );
        }

        this.order = this.storeFacade.order;
        if (this.order) {
            this.onOrderUpdated(this.order);
        }
    }

    onOrderUpdated(order: BaseOrder): void {
        if (order.eSign?.isAvailable === false) {
            this.wetSignOnly = true;
        }

        // Load bundle candidates for a readyToSend order so the "Choose Signing Method" bundle option
        // can enable when siblings exist (no-op/inert unless the facade exposes it — buy flow + feature).
        if (order.status === orderStatus.readyToSend) {
            this.storeFacade.actions.loadBundleCandidates?.dispatch({ orderId: order.id });
        }

        if (order.status === orderStatus.pendingSignatures) {
            // Form is not onboarded and the envelope is not sent yet
            if (
                Object.values(order.forms).some(form => form.isOnboarded === false) &&
                !order.eSign?.envelope?.status
            ) {
                this.viewState = 'map-signatures';
            } else {
                this.viewState = 'docu-sign';
                this.eSignWithWetSignEnabled = !!order.eSign?.wetSignRoles?.length;
                this.eSignWithWetSignCompleted = this.eSignWithWetSignEnabled
                    ? areFormUploadsCompletedInCurrentStep(order)
                    : true;
            }
        } else if (order.status === orderStatus.readyToSend) {
            if (Object.values(order.forms).some(form => form.isOnboarded === false)) {
                if (
                    order.eSign?.isSelected &&
                    order.eSign?.mappedSignatures?.status === 'Complete'
                ) {
                    this.viewState = 'docu-sign';
                } else if (order.eSign?.isSelected) {
                    this.viewState = 'map-signatures';
                } else if (order.eSign?.isSelected === false) {
                    this.viewState = 'wet-sign';
                } else {
                    this.viewState = 'preview';
                }
            } else {
                if (order.eSign?.isSelected) {
                    this.viewState = Object.keys(order.forms).some(
                        formId => order.forms[formId].isOnboarded === false
                    )
                        ? 'map-signatures'
                        : 'docu-sign';
                } else if (order.eSign?.isSelected === false) {
                    this.viewState = 'wet-sign';
                } else {
                    this.viewState = 'preview';
                }
            }
        }

        this.ref.detectChanges();
    }

    onClickESign() {
        const order = this.storeFacade.order;
        for (const file of order.files) {
            const isOnboarded = order.forms[file.formId as string]?.isOnboarded ?? true;

            if (
                (file.isUserUploaded && file.type === fileType.form && isOnboarded) ||
                (file.type === fileType.unassignedForm &&
                    file.orderStatus === orderStatus.readyToSend)
            ) {
                this.storeFacade.actions.removeDocument.dispatch({
                    orderId: order.id,
                    fileToRemove: file
                });
            }
        }
        this.viewState = 'preview';
        this.clearSigningMethod();
    }

    onClickContinue(): void {
        const order = this.storeFacade.order;
        if (order) {
            this.storeFacade.actions.signingComplete.dispatch({ orderId: order.id });
        }
    }

    onSelectSigningMethod(state: DetailViewState) {
        const order = this.storeFacade.order;

        // Bundling is not a single-order signing-method preference: show the bundle selection view
        // directly rather than persisting an eSign/wetsign preference on this order (aix-ui#5491).
        if (state === 'bundle') {
            this.bundleError = '';
            this.bundlePartialWarning = '';
            this.bundleOutcomeUnknown = false;
            this.selectedCandidateIds.clear();
            this.viewState = 'bundle';
            this.ref.detectChanges();
            return;
        }

        const signingMethod: { [key: string]: OrderSigningMethod } = {
            'docu-sign': 'eSign',
            'wet-sign': 'wetsign'
        };

        this.storeFacade.actions.signingMethod.dispatch({
            orderId: order.id,
            signingMethod: signingMethod[state]
        });
    }

    get hasBundleCandidates(): boolean {
        return this.bundleCandidates.length > 0;
    }

    toggleBundleCandidate(id: string) {
        if (this.selectedCandidateIds.has(id)) {
            this.selectedCandidateIds.delete(id);
        } else {
            this.selectedCandidateIds.add(id);
        }
    }

    // Loading/error states for the bundle send, shaped for the shared send form's [states] input.
    get bundleStates(): Observable<RemoteData<any, string | ErrorWrapper>>[] {
        return this.storeFacade.orderSendBundleRemoteData$
            ? [this.storeFacade.orderSendBundleRemoteData$]
            : [];
    }

    dismissBundleOutcome() {
        this.bundleOutcomeUnknown = false;
        this.bundlePartialWarning = '';
        this.ref.detectChanges();
    }

    onCancelBundle() {
        this.selectedCandidateIds.clear();
        this.bundleError = '';
        this.bundlePartialWarning = '';
        this.bundleOutcomeUnknown = false;
        this.viewState = 'preview';
        this.ref.detectChanges();
    }

    onSubmitBundle(form: DocuSignForm) {
        // In-flight guard: block a second concurrent send while one is already dispatched (set
        // synchronously so a rapid double-confirm cannot slip through before the store updates).
        if (this.bundleSendInProgress) {
            return;
        }
        // A bundle must be two or more orders: the backend rejects a lone anchor (needs >= 2 ids), so
        // block an empty selection in the UI rather than firing a guaranteed 400.
        if (this.selectedCandidateIds.size === 0) {
            this.bundleError = 'Select at least one other order to include in the bundle.';
            this.ref.detectChanges();
            return;
        }
        // All-or-nothing: the anchor order plus the ticked candidates, one investor email.
        this.bundleError = '';
        this.bundlePartialWarning = '';
        this.bundleOutcomeUnknown = false;
        this.bundleSendInProgress = true;
        this.storeFacade.actions.sendBundle?.dispatch({
            orderIds: [this.orderId, ...Array.from(this.selectedCandidateIds)],
            emailSubject: form.emailSubject,
            emailBody: form.emailBody
        });
    }

    clearSigningMethod() {
        const order = this.storeFacade.order;
        this.storeFacade.actions.signingMethod.dispatch({
            orderId: order.id,
            signingMethod: null
        });
        if (order.eSign?.mappedSignatures && order.eSign.envelope?.id) {
            this.storeFacade.actions.cancelEnvelope.dispatch({
                orderId: order.id,
                voidedReason: 'User cancelled'
            });
        }
    }

    setSignaturesCompleted() {
        const order = this.storeFacade.order;
        this.storeFacade.actions.getOrder.dispatch({
            orderId: order.id,
            reducerSuffix: this.reducerSuffix
        });
        this.signaturesCompleted = true;
        this.ref.detectChanges();
    }

    onSubmitDocuSign(form: DocuSignForm) {
        this.storeFacade.actions.sendDocusign.dispatch({ ...form, orderId: this.orderId });
    }

    onSubmitMapSignatures() {
        this.storeFacade.actions.mapSignatures.dispatch({ orderId: this.orderId });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.storeFacade.actions.sendDocusign.reset();
        this.storeFacade.actions.sendBundle?.reset();
        this.storeFacade.actions.loadBundleCandidates?.reset();
    }
}
